import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Empty,
  Form,
  Input,
  message,
  Modal,
  Pagination,
  Popconfirm,
  Skeleton,
  Space,
  Tabs,
  Tag,
  Typography,
} from "antd";
import {
  AppstoreOutlined,
  BankOutlined,
  CalendarOutlined,
  CreditCardOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import paths from "routes/paths";
import {
  deleteWorkspace as deleteWorkspaceRequest,
  fetchMemberWorkspaces,
  updateWorkspace,
} from "services/workspaceService";
import olreadiaService from "services/olreadiaService";
import identitySubscriptionService from "services/subscriptions";
import WorkspaceInvitations from "./WorkspaceInvitations";
import "./WorkspacesPage.css";
import {
  organizationName,
  formatDateTime,
  PAGE_SIZE,
  readable,
  statusColor,
  subscriptionLabel,
} from "./workspaceUtils";
import { OLIVEEB_COLORS, workspaceButtonStyles, workspaceStyles } from "./colors";

const { Text } = Typography;

const isPersonalAccount = (account) =>
  String(account?.accountType || "").toUpperCase() === "PERSONAL";

const oliveebButtonStyles = {
  view: {
    color: OLIVEEB_COLORS.oliveebBlue,
    background: OLIVEEB_COLORS.oliveebBlueSoft,
    borderColor: OLIVEEB_COLORS.oliveebBlueSoft,
  },
};

const SelectedWorkspaceSummary = () => {
  const navigate = useNavigate();
  const { selectedWorkspace } = useWorkspace();
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [selectedSubscription, setSelectedSubscription] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadSelectedOrganization = async () => {
      if (isPersonalAccount(selectedWorkspace) || !selectedWorkspace?.organizationId) {
        setSelectedOrganization(null);
        return;
      }

      try {
        const entity = await olreadiaService.organizations.get(
          selectedWorkspace.organizationId,
        );
        if (!cancelled) {
          setSelectedOrganization(entity || {});
        }
      } catch (error) {
        if (!cancelled) {
          setSelectedOrganization(null);
        }
      }
    };

    loadSelectedOrganization();

    return () => {
      cancelled = true;
    };
  }, [selectedWorkspace, selectedWorkspace?.organizationId]);

  useEffect(() => {
    let cancelled = false;

    const loadSelectedSubscription = async () => {
      if (!selectedWorkspace?.subscriptionId) {
        setSelectedSubscription(null);
        return;
      }

      try {
        const subscription = await identitySubscriptionService.subscriptions.get(
          selectedWorkspace.subscriptionId,
        );
        if (!cancelled) {
          setSelectedSubscription(subscription || {});
        }
      } catch (error) {
        if (!cancelled) {
          setSelectedSubscription(null);
        }
      }
    };

    loadSelectedSubscription();

    return () => {
      cancelled = true;
    };
  }, [selectedWorkspace?.subscriptionId]);

  return (
    <Card
      size="small"
      style={{
        borderRadius: 8,
        borderColor: selectedWorkspace
          ? OLIVEEB_COLORS.oliveebBlueSoft
          : OLIVEEB_COLORS.goldBorder,
        background: selectedWorkspace
          ? OLIVEEB_COLORS.bgSoft
          : OLIVEEB_COLORS.goldSoft,
        marginBottom: 16,
      }}
    >
      <div className="d-flex align-items-start gap-2 flex-wrap">
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            display: "grid",
            placeItems: "center",
            background: workspaceStyles.iconBubble.background,
            color: workspaceStyles.iconBubble.color,
            flex: "0 0 auto",
          }}
        >
          <AppstoreOutlined />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <Text
            style={{
              display: "block",
              fontSize: 12,
              color: OLIVEEB_COLORS.oliveebGreen,
            }}
          >
            Current workspace
          </Text>
          <Space size={6} align="center" wrap>
            <Text strong style={{ wordBreak: "break-word" }}>
              {selectedWorkspace?.name || "No workspace selected"}
            </Text>
            {selectedWorkspace?.id ? (
              <Button
                size="small"
                aria-label="View current workspace"
                title="View current workspace"
                icon={<EyeOutlined />}
                style={oliveebButtonStyles.view}
                onClick={() => navigate(paths.workspaceDetails(selectedWorkspace.id))}
              />
            ) : null}
          </Space>
          {selectedWorkspace ? (
            <Space wrap size={[4, 4]} style={{ marginTop: 6 }}>
              <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                {readable(selectedWorkspace.product, "Product")}
              </Tag>
              <Tag color="green" style={{ marginInlineEnd: 0 }}>
                {readable(selectedWorkspace.accountType, "Workspace type")}
              </Tag>
              {selectedWorkspace.subscriptionId ? (
                <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                  {subscriptionLabel(selectedSubscription, "Subscription")}
                </Tag>
              ) : null}
              {!isPersonalAccount(selectedWorkspace) && selectedWorkspace.organizationId ? (
                <Space size={4}>
                  <Tag color="cyan" style={{ marginInlineEnd: 0 }}>
                    {organizationName(selectedOrganization, "Organization")}
                  </Tag>
                  <Button
                    size="small"
                    aria-label="View current workspace organization"
                    title="View organization"
                    icon={<EyeOutlined />}
                    style={oliveebButtonStyles.view}
                    onClick={() => navigate(paths.workspaceOrganization(selectedWorkspace.id))}
                  />
                </Space>
              ) : null}
            </Space>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Use the top-right workspace picker to choose one.
            </Text>
          )}
        </div>
      </div>
    </Card>
  );
};

const WorkspacesTable = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshWorkspaces } = useWorkspace();
  const actorId = user?.id || user?.userId || "";
  const [accounts, setAccounts] = useState([]);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [editingAccount, setEditingAccount] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [organizationsById, setOrganizationsById] = useState({});
  const [subscriptionsById, setSubscriptionsById] = useState({});
  const [editForm] = Form.useForm();

  const loadAccounts = useCallback(async (nextPage = 1) => {
    if (!actorId) return;

    try {
      setLoading(true);
      const result = await fetchMemberWorkspaces({
        actorId,
        page: nextPage - 1,
        size: PAGE_SIZE,
      });
      setAccounts(result.content);
      setPage(nextPage);
      setTotal(result.totalElements ?? result.content.length);

      const organizationIds = Array.from(
        new Set(
          result.content
            .filter((account) => !isPersonalAccount(account))
            .map((account) => account.organizationId)
            .filter(Boolean),
        ),
      );
      if (organizationIds.length) {
        const settledEntities = await Promise.allSettled(
          organizationIds.map((id) => olreadiaService.organizations.get(id)),
        );

        setOrganizationsById((current) => {
          const next = { ...current };
          settledEntities.forEach((settled, index) => {
            const id = organizationIds[index];
            next[id] = settled.status === "fulfilled" ? settled.value || {} : null;
          });
          return next;
        });
      }

      const subscriptionIds = Array.from(
        new Set(result.content.map((account) => account.subscriptionId).filter(Boolean)),
      );
      if (subscriptionIds.length) {
        const settledSubscriptions = await Promise.allSettled(
          subscriptionIds.map((id) => identitySubscriptionService.subscriptions.get(id)),
        );

        setSubscriptionsById((current) => {
          const next = { ...current };
          settledSubscriptions.forEach((settled, index) => {
            const id = subscriptionIds[index];
            next[id] = settled.status === "fulfilled" ? settled.value || {} : null;
          });
          return next;
        });
      }
    } catch (error) {
      message.error(error?.message || "Failed to load workspaces.");
    } finally {
      setLoading(false);
    }
  }, [actorId]);

  const handleDeleteWorkspace = async (account) => {
    if (!account?.id) return;

    try {
      setDeletingId(account.id);
      await deleteWorkspaceRequest({ actorId, workspaceId: account.id });
      message.success("Workspace deleted.");
      await refreshWorkspaces();
      await loadAccounts(page);
    } catch (error) {
      message.error(error?.message || "Failed to delete workspace.");
    } finally {
      setDeletingId("");
    }
  };

  const openEditWorkspace = (account) => {
    setEditingAccount(account);
    editForm.setFieldsValue({
      name: account?.name || "",
      description: account?.description || "",
    });
  };

  const closeEditWorkspace = () => {
    setEditingAccount(null);
    editForm.resetFields();
  };

  const submitWorkspaceUpdate = async (values) => {
    if (!editingAccount?.id) return;

    const name = String(values.name || "").trim();
    const description = String(values.description || "").trim();
    if (!name) {
      message.error("Enter a workspace name.");
      return;
    }

    try {
      setUpdating(true);
      const updatedAccount = await updateWorkspace({
        actorId,
        workspaceId: editingAccount.id,
        name,
        description,
      });
      setAccounts((current) =>
        current.map((account) =>
          account.id === editingAccount.id
            ? { ...account, ...(updatedAccount || {}), name, description }
            : account,
        ),
      );
      await refreshWorkspaces();
      message.success("Workspace updated.");
      closeEditWorkspace();
    } catch (error) {
      message.error(error?.message || "Failed to update workspace.");
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    loadAccounts(1);
  }, [loadAccounts]);

  const filteredAccounts = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return accounts;

    return accounts.filter((account) => {
      const values = [
        account.name,
        account.description,
        account.product,
        account.accountType,
        account.organizationId,
        organizationName(
          organizationsById[account.organizationId],
          account.organizationId,
        ),
        account.subscriptionId,
        subscriptionLabel(subscriptionsById[account.subscriptionId], account.subscriptionId),
      ];
      return values.some((value) => String(value || "").toLowerCase().includes(term));
    });
  }, [accounts, organizationsById, query, subscriptionsById]);

  const workspaceTotalLabel = query
    ? `${filteredAccounts.length} matching workspace${filteredAccounts.length === 1 ? "" : "s"}`
    : `${total} workspace${total === 1 ? "" : "s"}`;

  const activeMemberCount = (users = []) =>
    users.filter((member) => {
      const status = String(member.membershipStatus || "").toUpperCase();
      return status === "ACTIVE" || status === "ACCEPTED" || status === "";
    }).length;

  const membershipStatuses = (account) =>
    Array.from(
      new Set((account.users || []).map((member) => member.membershipStatus).filter(Boolean)),
    ).slice(0, 3);

  const openWorkspace = (account) => {
    if (account?.id) navigate(paths.workspaceDetails(account.id));
  };

  const renderWorkspaceCard = (account) => {
    const showOrganization = !isPersonalAccount(account) && account.organizationId;
    const entity = organizationsById[account.organizationId];
    const entityLabel = showOrganization
      ? organizationName(entity, "Organization")
      : "";
    const subscription = subscriptionsById[account.subscriptionId];
    const subscriptionText = account.subscriptionId
      ? subscriptionLabel(subscription, "Subscription")
      : "Not linked";
    const statuses = membershipStatuses(account);
    const memberCount = activeMemberCount(account.users);

    return (
      <article
        key={account.id || account.name}
        className="workspace-row-card"
      >
        <div className="workspace-row-card__main">
          <div className="workspace-row-card__icon" aria-hidden="true">
            {showOrganization ? <BankOutlined /> : <AppstoreOutlined />}
          </div>
          <div className="workspace-row-card__copy">
            <div className="workspace-row-card__title-row">
              <Text className="workspace-row-card__eyebrow">
                {readable(account.product, "Product")} workspace
              </Text>
              <Space wrap size={[5, 5]}>
                <Tag color="green" className="workspace-row-card__tag">
                  {readable(account.accountType, "Workspace type")}
                </Tag>
                {statuses.map((status) => (
                  <Tag key={status} color={statusColor(status)} className="workspace-row-card__tag">
                    {readable(status)}
                  </Tag>
                ))}
              </Space>
            </div>
            <Text strong className="workspace-row-card__title">
              {account.name || "Workspace"}
            </Text>
            <Text className="workspace-row-card__description">
              {account.description || "No description has been added yet."}
            </Text>
          </div>
        </div>

        <div className="workspace-row-card__details">
          <div className="workspace-row-card__metric">
            <span><TeamOutlined /> Members</span>
            <strong>{memberCount}</strong>
          </div>
          <div className="workspace-row-card__metric">
            <span><CreditCardOutlined /> Subscription</span>
            <strong>{subscriptionText}</strong>
          </div>
          <div className="workspace-row-card__metric">
            <span><BankOutlined /> Organization</span>
            <strong>{showOrganization ? entityLabel : "Personal workspace"}</strong>
          </div>
          <div className="workspace-row-card__metric">
            <span><CalendarOutlined /> Created</span>
            <strong>{formatDateTime(account.createdAt) || "-"}</strong>
          </div>
        </div>

        <div className="workspace-row-card__actions">
          {showOrganization ? (
            <Button
              title="View organization"
              icon={<BankOutlined />}
              style={workspaceButtonStyles.organization}
              onClick={() => {
                if (account.id) navigate(paths.workspaceOrganization(account.id));
              }}
            />
          ) : null}
          <Button
            title="View workspace"
            icon={<EyeOutlined />}
            style={oliveebButtonStyles.view}
            onClick={() => openWorkspace(account)}
          />
          <Button
            title="Edit workspace"
            icon={<EditOutlined />}
            style={{
              borderColor: OLIVEEB_COLORS.oliveebBlue,
              color: OLIVEEB_COLORS.oliveebBlue,
            }}
            onClick={() => openEditWorkspace(account)}
          />
          <Popconfirm
            title="Delete workspace?"
            description="This action cannot be undone."
            okText="Delete"
            okButtonProps={{ danger: true, loading: deletingId === account.id }}
            onConfirm={() => handleDeleteWorkspace(account)}
          >
            <Button
              danger
              title="Delete workspace"
              icon={<DeleteOutlined />}
              loading={deletingId === account.id}
            />
          </Popconfirm>
        </div>
      </article>
    );
  };

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
        <Space wrap>
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: OLIVEEB_COLORS.placeholder }} />}
            placeholder="Search workspaces"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            style={{ width: 280, borderRadius: 8 }}
          />
        </Space>
        <Space wrap>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate(paths.workspaceCreate())}
            style={{
              borderColor: OLIVEEB_COLORS.oliveebGreen,
              background: OLIVEEB_COLORS.oliveebGreen,
              fontWeight: 700,
            }}
          >
            Add Workspace
          </Button>
          <Button
            icon={<ReloadOutlined />}
            style={workspaceButtonStyles.refresh}
            onClick={() => loadAccounts(page)}
          >
            Refresh
          </Button>
        </Space>
      </div>

      <section className="workspace-cards-panel">
        <div className="workspace-cards-panel__header">
          <div>
            <Text strong className="workspace-cards-panel__title">Workspace directory</Text>
            <Text className="workspace-cards-panel__subtitle">
              {workspaceTotalLabel}
            </Text>
          </div>
          <Text className="workspace-cards-panel__hint">
            Use the action icons to open or manage a workspace
          </Text>
        </div>

        {loading ? (
          <div className="workspace-cards-list">
            {[0, 1, 2].map((item) => (
              <div className="workspace-row-card workspace-row-card--loading" key={item}>
                <Skeleton active avatar paragraph={{ rows: 3 }} />
              </div>
            ))}
          </div>
        ) : filteredAccounts.length ? (
          <>
            <div className="workspace-cards-list">
              {filteredAccounts.map(renderWorkspaceCard)}
            </div>
            <div className="workspace-cards-pagination">
              <Pagination
                current={query ? 1 : page}
                pageSize={PAGE_SIZE}
                total={query ? filteredAccounts.length : total}
                showSizeChanger={false}
                onChange={(nextPage) => loadAccounts(nextPage)}
              />
            </div>
          </>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No workspaces found"
            className="workspace-cards-empty"
          />
        )}
      </section>
      <Modal
        title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Edit Workspace</Text>}
        open={Boolean(editingAccount)}
        onCancel={closeEditWorkspace}
        onOk={() => editForm.submit()}
        okText="Save"
        confirmLoading={updating}
        destroyOnHidden
      >
        <Form
          form={editForm}
          layout="vertical"
          requiredMark={false}
          onFinish={submitWorkspaceUpdate}
        >
          <Form.Item
            label="Workspace name"
            name="name"
            rules={[{ required: true, message: "Enter workspace name." }]}
          >
            <Input placeholder="Workspace name" autoFocus />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={3} placeholder="Optional workspace description" />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

const WorkspacesPage = () => (
  <div className="project-page workspaces-page">
    <div className="workspaces-page__shell">
      <div className="workspaces-page__content">
        <div className="workspaces-page__header d-flex justify-content-between align-items-center gap-3 flex-wrap">
          <WidgetSectionTitle
            icon={<AppstoreOutlined />}
            title="Workspaces"
            subtitle="Review product access, memberships, subscriptions, and organization links tied to your workspaces"
            className="flex-1"
            titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
            subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
          />
        </div>

        <main className="workspaces-page__main">
          <SelectedWorkspaceSummary />

          <Tabs
            className="pa-top-tabs workspaces-page__tabs"
            defaultActiveKey="accounts"
            items={[
              {
                key: "accounts",
                label: "Workspaces",
                children: <WorkspacesTable />,
              },
              {
                key: "invitations",
                label: "Invitations",
                children: <WorkspaceInvitations />,
              },
            ]}
          />
        </main>
      </div>
    </div>
  </div>
);

export default WorkspacesPage;
